import { useEffect, useState } from 'react';

import { Label, Typography } from '@remarkable/ark-web';
import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';

import { queryKeys } from 'src/api/queries';
import {
  useEnterpriseEnrollmentStatus,
  useEnterpriseUpdateConnection,
} from 'src/api/queries/enterpriseEnrollment';
import {
  Button,
  Divider,
  Feedback,
  Form,
  Input,
  NotificationBox,
  Sheet,
  Table,
  TableCell,
  TableHeader,
  Tooltip,
} from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { CopyText } from 'src/components/CopyText';
import {
  extractSamlCertificateMetadata,
  getSamlCertificateValidationError,
  validateSamlSignInUrl,
} from 'src/utils/samlValidators';
import type { SamlCertificateMetadata } from 'src/utils/samlValidators';
import { URLS } from 'src/utils/urls/urls';

import { FileIcon, FileXIcon } from '@phosphor-icons/react';
import { Checkmark, Export } from '@remarkable/ark-icons';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { CancelSetupModal } from '../components/CancelSetup';
import { EnterpriseSteps } from '../components/EnterpriseSteps';

interface FormData {
  signInUrl: string;
  samlCertificate: FileList;
}

const routeApi = getRouteApi('/_auth/enterprise/enroll/setup-saml');

export function SetupSamlPage() {
  const enrollmentStatus = useEnterpriseEnrollmentStatus();
  const navigate = routeApi.useNavigate();
  const submitSamlCertificate = useEnterpriseUpdateConnection();
  const queryClient = useQueryClient();
  const [certificateMetadata, setCertificateMetadata] =
    useState<SamlCertificateMetadata | null>(null);

  const form = useForm<FormData>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (!enrollmentStatus.data) return;

    form.reset({
      signInUrl: enrollmentStatus.data.signInUrl,
    });
  }, [enrollmentStatus.data, form]);

  const samlCertificateFile = form.watch('samlCertificate');

  useEffect(() => {
    let isCancelled = false;

    const processCertificate = async () => {
      if (!samlCertificateFile?.length) {
        setCertificateMetadata(null);
        return;
      }

      await form.trigger('samlCertificate');

      const uploadedFile = samlCertificateFile[0];
      if (!uploadedFile) {
        setCertificateMetadata(null);
        return;
      }

      const certificateContent = await uploadedFile.text();
      const metadata = await extractSamlCertificateMetadata(certificateContent);

      if (!isCancelled) {
        setCertificateMetadata(metadata);
      }
    };

    processCertificate().catch(() => {
      if (!isCancelled) {
        setCertificateMetadata(null);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [form, samlCertificateFile]);

  const onSubmit = async (data: FormData) => {
    const certificate = samlCertificateFile?.[0]
      ? await samlCertificateFile[0].text()
      : enrollmentStatus.data?.base64Cert;

    if (!certificate) {
      // TODO: Should there be an error thrown here?
      return;
    }

    const isCertificateUnchanged =
      enrollmentStatus.data?.base64Cert === certificate;
    const isSignInUrlUntouched =
      enrollmentStatus.data?.signInUrl === data.signInUrl;
    if (isCertificateUnchanged && isSignInUrlUntouched) {
      return navigate({ to: '../verify-saml' });
    }

    submitSamlCertificate.mutate(
      {
        signInUrl: data.signInUrl,
        certificate: certificate,
      },
      {
        async onSettled() {
          await queryClient.invalidateQueries({
            queryKey: queryKeys.enterpriseEnrollment.status,
          });
        },
        onSuccess() {
          return navigate({ to: '../verify-saml' });
        },
      }
    );
  };

  const hasCertificate = !!(
    samlCertificateFile?.[0] ?? enrollmentStatus.data?.base64Cert
  );
  const fileName =
    samlCertificateFile?.[0]?.name ||
    (enrollmentStatus.data?.base64Cert ? 'Certificate' : 'No file');

  return (
    <AppPage.Container>
      <AppPage.Header
        title="Setting up Enterprise SSO"
        className="max-w-l2xl text-center"
      />
      <AppPage.Content className="max-w-ll">
        <EnterpriseSteps />

        <Sheet className="gap-24">
          <Typography as="h2" variant="heading-sm">
            Connect your identity provider
          </Typography>

          <Typography variant="body-md">
            To securely connect your identity provider to our application and
            enable single sign-on, you&apos;ll need to share SAML URLs and a
            certificate.
          </Typography>

          <Divider />

          <div>
            <Typography variant="body-md" fontWeight="medium">
              1. Copy the entity ID and add it to your identity provider
            </Typography>
            <Typography variant="body-sm">
              This is used by your identity provider to recognize your
              application.
            </Typography>
            <CopyText
              value={enrollmentStatus.data?.entityId}
              aria-label="Copy entity ID"
            />
          </div>

          <div>
            <Typography variant="body-md" fontWeight="medium">
              2. Copy reply/ACS URL and add it to your identity provider
            </Typography>
            <Typography variant="body-sm">
              Your identity provider will send login information to this URL.
            </Typography>
            <CopyText
              value={enrollmentStatus.data?.replyUrl}
              aria-label="Copy reply URL"
            />
          </div>

          <Form
            name="setup-saml"
            {...form}
            onSubmit={onSubmit}
            className="flex flex-col gap-24"
          >
            <Input
              name="signInUrl"
              label="3. Add the sign-in URL"
              type="url"
              validate={validateSamlSignInUrl}
              description={
                <Typography variant="body-sm">
                  This is the address users will use to log in with single
                  sign-on. You can find this in your identity provider.
                </Typography>
              }
              placeholder="https://example.com/saml/login?SAMLRequest=..."
              required
            />

            <div>
              <Label htmlFor="samlCertificate">
                4. Upload your SAML certificate in base64 encoded PEM or CER
                format
              </Label>
              <Typography variant="body-sm">
                This certificate ensures secure communication between your
                system and the identity provider. You can find the certificate
                in your identity provider. If you have a DER encoded
                certificate, you can convert it to PEM format using OpenSSL.
              </Typography>

              <Tooltip
                asChild
                title={'Certificate received'}
                enabled={!!enrollmentStatus.data?.base64Cert}
              >
                <label
                  htmlFor="samlCertificate"
                  className={clsx(
                    'group flex h-48 w-full cursor-pointer items-end justify-between gap-24'
                  )}
                  data-cy="saml-certificate-field"
                >
                  <div className="rounded-sm flex h-48 w-full items-center justify-between gap-8 border border-dashed border-neutral-light-6 px-16 focus-within:outline focus-within:outline-pen-blue">
                    <div className="flex items-center gap-8">
                      {hasCertificate ? (
                        <FileIcon
                          className="inline shrink-0 text-neutral-dark-1"
                          size={24}
                          weight="light"
                          data-cy="saml-certificate-icon"
                        />
                      ) : (
                        <FileXIcon
                          className="inline shrink-0 text-neutral-dark-1"
                          size={24}
                          weight="light"
                        />
                      )}
                      <Typography variant="interface-md" className="truncate">
                        {fileName}
                      </Typography>
                      <input
                        id="samlCertificate"
                        className="w-0 opacity-0"
                        type="file"
                        data-cy="saml-certificate-input"
                        accept=".pem,.cer"
                        {...form.register('samlCertificate', {
                          required: !enrollmentStatus.data?.base64Cert,
                          validate: async (fileList) => {
                            const validationError =
                              await getSamlCertificateValidationError(
                                fileList,
                                enrollmentStatus.data?.base64Cert
                              );

                            return validationError ?? true;
                          },
                        })}
                      />
                    </div>

                    {enrollmentStatus.data?.base64Cert && (
                      <Checkmark
                        size={24}
                        className="text-pen-blue"
                        data-cy="saml-certificate-submitted-icon"
                      />
                    )}
                  </div>

                  {!enrollmentStatus.data?.base64Cert && (
                    <div
                      role="button"
                      tabIndex={-1}
                      className="ark-button ark-button-large ark-button--tertiary group-focus-within:bg-pen-blue-light"
                    >
                      <span>Upload</span>
                      <Export size={24} />
                    </div>
                  )}
                </label>
              </Tooltip>

              {form.formState.errors.samlCertificate?.message && (
                <Typography
                  variant="interface-xs"
                  leadingNone
                  className="mt-8 text-feedback-red-500"
                >
                  {form.formState.errors.samlCertificate.message.toString()}
                </Typography>
              )}

              {certificateMetadata &&
                !form.formState.errors.samlCertificate?.message && (
                  <div className="rounded-sm mt-12 border border-neutral-light-8 bg-neutral-light-3 p-12">
                    <Typography variant="interface-xs" className="font-medium">
                      Thumbprint
                    </Typography>
                    <Typography variant="interface-sm" className="break-all">
                      {certificateMetadata.thumbprint}
                    </Typography>

                    <Typography
                      variant="interface-xs"
                      className="mt-8 font-medium"
                    >
                      Subject
                    </Typography>
                    <Typography variant="interface-sm" className="break-all">
                      {certificateMetadata.subject}
                    </Typography>

                    <Typography
                      variant="interface-xs"
                      className="mt-8 font-medium"
                    >
                      Expiry date
                    </Typography>
                    <Typography variant="interface-sm">
                      {certificateMetadata.expiryDate}
                    </Typography>
                  </div>
                )}
            </div>

            <div>
              <Typography variant="body-md" className="font-medium">
                5. Attribute mapping
              </Typography>

              <Typography variant="body-sm" className="text-pretty">
                For successful login, your identity provider needs the following
                attributes to be correctly set up.
              </Typography>

              <Table className="rounded-sm mt-16 border-separate border-spacing-0 overflow-hidden border border-neutral-light-8">
                <colgroup>
                  <col className="w-1/3" />
                  <col className="w-2/3" />
                </colgroup>
                <thead className="bg-neutral-light-3">
                  <tr>
                    <TableHeader>
                      <Typography
                        variant="interface-sm"
                        fontWeight="semibold"
                        leadingNone
                      >
                        Name
                      </Typography>
                    </TableHeader>
                    <TableHeader>
                      <Typography
                        variant="interface-sm"
                        fontWeight="semibold"
                        leadingNone
                      >
                        Value
                      </Typography>
                    </TableHeader>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <TableCell>
                      <code>email</code>
                    </TableCell>
                    <TableCell>The user&apos;s email address</TableCell>
                  </tr>
                  <tr>
                    <TableCell>
                      <code>family_name</code>
                    </TableCell>
                    <TableCell>
                      The user&apos;s last name (will be displayed in the
                      overview)
                    </TableCell>
                  </tr>
                  <tr>
                    <TableCell className="border-b-0">
                      <code>given_name</code>
                    </TableCell>
                    <TableCell className="border-b-0">
                      The user&apos;s first name (will be displayed in the
                      overview)
                    </TableCell>
                  </tr>
                </tbody>
              </Table>
            </div>

            <div className="flex w-full flex-col-reverse flex-wrap-reverse gap-24 lm:flex-row">
              <div className="flex flex-1 ls:justify-center lm:justify-start">
                <CancelSetupModal
                  componentLocation={
                    ComponentLocations.ENTERPRISE.ENROLL_SETUP_SAML
                  }
                  trigger={
                    <Button
                      type="button"
                      variant="tertiary-neutral"
                      className="w-full ls:w-fit"
                      analytics={{
                        location:
                          ComponentLocations.ENTERPRISE.ENROLL_SETUP_SAML,
                        text: `Cancel`,
                        action: `cancel`,
                      }}
                    >
                      Cancel
                    </Button>
                  }
                />
              </div>

              <div className="flex flex-col-reverse flex-wrap-reverse gap-24 ls:flex-row">
                <Button
                  as="a"
                  variant="secondary"
                  to={URLS.ENTERPRISE_VERIFY_DOMAIN}
                  className="w-full ls:w-fit ls:flex-1 lm:flex-initial"
                  analytics={{
                    location: ComponentLocations.ENTERPRISE.ENROLL_SETUP_SAML,
                    text: `Previous`,
                    action: `previous`,
                  }}
                >
                  Previous
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={submitSamlCertificate.isPending}
                  disabled={submitSamlCertificate.isPending}
                  className="w-full ls:w-fit ls:flex-1 lm:flex-initial"
                  analytics={{
                    location: ComponentLocations.ENTERPRISE.ENROLL_SETUP_SAML,
                    text: `Continue`,
                    action: `continue`,
                  }}
                >
                  Continue
                </Button>
              </div>
            </div>
          </Form>

          <NotificationBox error={submitSamlCertificate.error} />
        </Sheet>
        <Feedback
          className="mt-48"
          feedbackTargetId="enterprise-enroll-saml-setup"
          title="Did you find what you were looking for?"
        />
      </AppPage.Content>
    </AppPage.Container>
  );
}
